/**
 * Centralized HTTP Client -- Xyden Leadership Assessment Platform
 */

import { apiUrl } from '../config/api';
import { useAuthStore } from '../stores/authStore';

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const state = useAuthStore.getState();
  if (state.token) headers['Authorization'] = `Bearer ${state.token}`;
  if (state.sessionId) headers['X-Session-Id'] = state.sessionId;
  return headers;
}

export interface ReferenceImpact {
  entity_type: string;
  field: string;
  count: number;
  sample_ids: string[];
  reason?: string;
}

export class ServiceError extends Error {
  /** Backend error code (e.g. ENTITY_HAS_DEPENDENCIES) when present in detail.error. */
  public errorCode: string | null;
  /** Structured RI block payload — only set when the backend returned 409
   *  with detail.error === 'ENTITY_HAS_DEPENDENCIES' and a detail.impacts array. */
  public impacts: ReferenceImpact[] | null;
  /** Full parsed detail object (when present and a dict). Lets callers
   *  read any structured field the backend attached, e.g. importer errors[]. */
  public details: Record<string, unknown> | null;

  constructor(
    public status: number,
    message: string,
    options: {
      errorCode?: string | null;
      impacts?: ReferenceImpact[] | null;
      details?: Record<string, unknown> | null;
    } = {},
  ) {
    super(message);
    this.name = 'ServiceError';
    this.errorCode = options.errorCode ?? null;
    this.impacts = options.impacts ?? null;
    this.details = options.details ?? null;
  }
}

/**
 * Backend error code (``detail.error``) returned when a participant-facing
 * email could not be delivered — test create/launch invitations or a single
 * reminder. The accompanying ``message`` names the failed recipient(s). The
 * backend maps it to a 502 via a dedicated EmailDeliveryError handler so the
 * admin UI can surface a bounced email instead of silently reporting success.
 * (gap STB-009)
 */
export const EMAIL_DELIVERY_ERROR_CODE = 'EMAIL_DELIVERY_FAILED';

/**
 * True when ``err`` is a ServiceError carrying the email-delivery failure code,
 * i.e. the backend reported that one or more participant emails could not be
 * sent. Narrows to ServiceError so callers can read ``.message`` — which names
 * the failed recipient(s) — when composing the user-facing toast.
 */
export function isEmailDeliveryError(err: unknown): err is ServiceError {
  return err instanceof ServiceError && err.errorCode === EMAIL_DELIVERY_ERROR_CODE;
}

/**
 * Thrown when a request exceeds its timeout. Deliberately NOT a
 * ServiceError: ``isTransientServerError`` treats any non-ServiceError as
 * transient, so a timeout on a retry-wrapped call (indefinite-load GETs,
 * bounded final-submit) is retried rather than surfaced as a hard failure.
 * Bare ``await`` callers (per-question submit, pause) get a clear message.
 */
export class TimeoutError extends Error {
  constructor(public timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms. Please check your connection and try again.`);
    this.name = 'TimeoutError';
  }
}

/**
 * Default per-request timeout. Browser ``fetch`` has NO built-in timeout —
 * a half-open connection (dead server holding the socket, captive proxy)
 * hangs the promise forever, which previously stranded the candidate AQ
 * flow on a permanently-disabled "Saving…" button. Every request now aborts
 * at this ceiling so the hang becomes a normal rejection the caller can
 * handle (toast, retry, or spinner-with-retry).
 */
export const DEFAULT_TIMEOUT_MS = 30_000;

export interface RequestOptions {
  /** Per-call timeout override (ms). 0 disables the timeout (e.g. large uploads). */
  timeoutMs?: number;
  /** Caller AbortSignal — composed with the timeout; whichever fires first wins. */
  signal?: AbortSignal;
}

/**
 * Build an AbortSignal that fires when EITHER the timeout elapses or the
 * caller's signal aborts. Returns the combined signal, a ``cleanup`` to
 * clear the timer + listeners, and ``didTimeout`` so the caller can
 * attribute an abort to the timeout (→ TimeoutError) vs. an external
 * cancellation (→ propagate the original AbortError).
 */
function withTimeout(externalSignal: AbortSignal | undefined, timeoutMs: number) {
  const controller = new AbortController();
  let timedOut = false;

  const timer =
    timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs)
      : null;

  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort);
    }
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      if (timer !== null) clearTimeout(timer);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    },
  };
}

/**
 * Force the user back to /login when — and only when — the session is
 * genuinely no longer usable. A 401 means the token is expired/missing, so
 * the user must re-auth; ``logout()`` clears the stored token and the
 * AuthCheckProvider redirect kicks the UI to /login.
 *
 * Deliberately scoped:
 *   - We do NOT logout on 403 RBAC_UNAUTHORIZED. A 403 only proves "this
 *     endpoint isn't allowed for your current role," which is a normal RBAC
 *     outcome for legitimately-authenticated users whose role set doesn't
 *     cover a particular endpoint (e.g. a Content Admin hitting a
 *     Super-Admin-only route). Treating that as session drift would log such
 *     a user out the moment any page silently calls an endpoint above their
 *     role — see ContentStudioVersionsPage's prior /api/users call. If we
 *     later need to handle real session drift (e.g. role change in another
 *     tab), the right detector is the /api/auth/me poll in AuthCheckProvider,
 *     not data-endpoint 403s.
 *   - The /api/auth/login path is skipped — a 401 there means bad
 *     credentials, not an expired session.
 *   - It only acts when a token is actually held, so an already-logged-out
 *     state is a no-op.
 *
 * Shared by ``handleResponse`` (JSON verbs + ``postFile``) and ``getBlob``
 * (binary/CSV/PPTX/PDF exports) so no fetch wrapper can drift from this
 * contract: every 401, on every transfer kind, clears the dead session.
 */
function maybeLogoutOnAuthFailure(status: number, url?: string, errorCode?: string | null): void {
  const isLoginRequest = url ? url.includes('/api/auth/login') : false;
  const isSessionInvalid = status === 401;
  if (isSessionInvalid && !isLoginRequest && useAuthStore.getState().token) {
    if (errorCode === 'SESSION_SUPERSEDED') {
      // Don't logout yet — show a blocking dialog so the user knows why.
      // The dialog's button will call logout() and redirect to /login.
      useAuthStore.getState().setSessionKicked();
      return;
    }
    const reason = errorCode === 'AUTH_TOKEN_EXPIRED'
      ? 'Your session has expired. Please sign in again.'
      : 'You have been signed out. Please sign in again.';
    useAuthStore.getState().logout(reason);
  }
}

async function handleResponse<T>(response: Response, url?: string): Promise<T> {
  const text = await response.text();
  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode: string | null = null;
    let impacts: ReferenceImpact[] | null = null;
    let details: Record<string, unknown> | null = null;
    try {
      const body = JSON.parse(text) as {
        detail?: { error?: string; message?: string; impacts?: ReferenceImpact[]; [k: string]: unknown } | string;
        error?: string;
        message?: string;
      };
      const detail = body.detail;
      if (detail && typeof detail === 'object') {
        details = detail as Record<string, unknown>;
        if (detail.message) message = detail.message;
        if (detail.error) errorCode = detail.error;
        if (Array.isArray(detail.impacts)) impacts = detail.impacts;
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (body.message) {
        message = body.message;
      } else if (body.error) {
        message = body.error;
      }
    } catch { /* use default message */ }

    maybeLogoutOnAuthFailure(response.status, url, errorCode);
    throw new ServiceError(response.status, message, { errorCode, impacts, details });
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ServiceError(response.status, `Invalid JSON response`);
  }
}

/**
 * Run a fetch-driven operation under a timeout/abort envelope and normalise
 * the failure modes: a timeout-driven abort becomes a ``TimeoutError``
 * (transient), while an external-signal abort or a network error propagates
 * unchanged. The ``run`` callback receives the composed signal and performs
 * the fetch + body read inside the envelope, so a stalled body read (JSON,
 * blob, or error detail) is covered by the timeout — not just the initial
 * connect. Shared by ``fetchJson`` (JSON verbs), ``postFile`` (multipart
 * upload), and ``getBlob`` (binary download) so all three enforce identical
 * timeout semantics rather than each hand-rolling the envelope.
 */
async function withTimeoutEnvelope<T>(
  options: RequestOptions | undefined,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const { signal, didTimeout, cleanup } = withTimeout(options?.signal, timeoutMs);
  try {
    return await run(signal);
  } catch (err) {
    if (didTimeout()) throw new TimeoutError(timeoutMs);
    throw err;
  } finally {
    cleanup();
  }
}

async function fetchJson<T>(url: string, init: RequestInit, options?: RequestOptions): Promise<T> {
  return withTimeoutEnvelope(options, async (signal) => {
    const response = await fetch(url, { ...init, signal });
    return handleResponse<T>(response, url);
  });
}

export const apiClient = {
  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return fetchJson<T>(apiUrl(path), { headers: { ...authHeaders() } }, options);
  },

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return fetchJson<T>(
      apiUrl(path),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      options,
    );
  },

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return fetchJson<T>(
      apiUrl(path),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      options,
    );
  },

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return fetchJson<T>(
      apiUrl(path),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      },
      options,
    );
  },

  async delete<T = void>(path: string, options?: RequestOptions): Promise<T> {
    return fetchJson<T>(apiUrl(path), { method: 'DELETE', headers: { ...authHeaders() } }, options);
  },

  // Multipart upload for binary files (e.g., .xlsx imports). The single
  // file is sent under the configured form field name; response is parsed
  // as JSON via the standard handleResponse path so structured errors
  // (including ServiceError.impacts and any other detail.* fields) flow
  // through unchanged. Runs under the same timeout envelope as the JSON
  // verbs (30s default) so a dead/half-open socket rejects with TimeoutError
  // instead of hanging the import forever; pass ``{ timeoutMs: 0 }`` to
  // disable the ceiling for a genuinely large upload.
  async postFile<T>(path: string, file: File, fieldName: string = 'file', options?: RequestOptions): Promise<T> {
    const url = apiUrl(path);
    const form = new FormData();
    form.append(fieldName, file);
    return withTimeoutEnvelope(options, async (signal) => {
      const response = await fetch(url, {
        method: 'POST',
        headers: { ...authHeaders() },
        body: form,
        signal,
      });
      return handleResponse<T>(response, url);
    });
  },

  // Returns the raw response body as a Blob. Use for binary/CSV/zip downloads
  // where handleResponse's JSON.parse would fail. Runs under the same timeout
  // envelope as the JSON verbs (30s default) so a stalled export download
  // rejects with TimeoutError instead of hanging forever; the blob read is
  // inside the envelope so a half-delivered body is covered too.
  async getBlob(path: string, options?: RequestOptions): Promise<{ blob: Blob; filename: string | null }> {
    const url = apiUrl(path);
    return withTimeoutEnvelope(options, async (signal) => {
      const response = await fetch(url, { headers: { ...authHeaders() }, signal });
      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const text = await response.text();
          const body = JSON.parse(text) as { detail?: { message?: string } | string; message?: string };
          if (body.detail && typeof body.detail === 'object' && body.detail.message) message = body.detail.message;
          else if (typeof body.detail === 'string') message = body.detail;
          else if (body.message) message = body.message;
        } catch { /* keep default */ }
        // Mirror handleResponse: a 401 on a binary/CSV/PPTX/PDF export must
        // clear the dead session too, not just JSON verbs.
        maybeLogoutOnAuthFailure(response.status, url);
        throw new ServiceError(response.status, message);
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const filename = match ? match[1] : null;
      const blob = await response.blob();
      return { blob, filename };
    });
  },
};
